import React, { useState, useEffect } from 'react';
import { Menu, X, Layers } from 'lucide-react';
import { getSymbologyDetails } from '../utils/layerStyleMapper';

const Sidebar = ({ onLayerToggle, onToolSelect }) => {
  const [layers, setLayers] = useState([]);
  const [activeLayers, setActiveLayers] = useState({}); // Changed to object for multi-selection
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch layers from API
  useEffect(() => {
    const fetchLayers = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://backend.geonetra.tech/api/v1/fgb?offset=0&limit=100');
        const result = await response.json();

        if (result.data) {
          setLayers(result.data);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchLayers();
  }, []);


  const toggleLayer = (layerId) => {

    // Toggle layer selection (multi-selection enabled)
    const newActiveLayers = { ...activeLayers };

    if (newActiveLayers[layerId]) {
      // Deselect layer
      delete newActiveLayers[layerId];
    } else {
      // Select layer
      newActiveLayers[layerId] = true;
    }

    setActiveLayers(newActiveLayers);

    // Get all active layer data for metadata
    const activeLayerData = {};
    Object.keys(newActiveLayers).forEach(id => {
      const layerData = layers.find(l => l.id === id);
      if (layerData) {
        activeLayerData[id] = layerData;
      }
    });

    onLayerToggle && onLayerToggle(newActiveLayers, activeLayerData);
  };

  const LayerButton = ({ layerKey, label, layerData }) => {
    const isActive = activeLayers[layerKey];
    const symbology = getSymbologyDetails(layerData);

    // Render the exact symbology from the map style
    const renderSymbology = () => {
      const { fillColor, strokeColor, geometryType } = symbology;

      if (geometryType === 'polygon') {
        // Square for polygons with fill and stroke
        return (
          <div
            className="w-4 h-4 flex-shrink-0 shadow-sm"
            style={{
              backgroundColor: fillColor || 'transparent',
              border: strokeColor ? `2px solid ${strokeColor}` : '2px solid white',
            }}
            title="Polygon layer"
          />
        );
      } else if (geometryType === 'line') {
        // Line for linear features
        return (
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" title="Line layer">
            <div
              className="w-full shadow-sm"
              style={{
                height: '3px',
                backgroundColor: strokeColor || '#6b7280',
              }}
            />
          </div>
        );
      } else {
        // Circle for points with fill and stroke
        return (
          <div
            className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm"
            style={{
              backgroundColor: fillColor || '#9ca3af',
              border: strokeColor ? `2px solid ${strokeColor}` : '2px solid white',
            }}
            title="Point layer"
          />
        );
      }
    };

    return (
      <button
        onClick={() => toggleLayer(layerKey)}
        className={`w-full flex items-center space-x-3 text-sm p-2 md:p-3 rounded-lg transition-all ${
          isActive
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
        }`}
      >
        {renderSymbology()}
        <span className="text-xs md:text-sm font-medium flex-1 text-left">{label}</span>
      </button>
    );
  };

  return (
    <>
      {/* Mobile Menu Button - Only show Menu icon when closed */}
      {!isMobileMenuOpen && (
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="md:hidden fixed top-20 left-4 z-50 bg-slate-900 text-white p-2 rounded-lg shadow-lg"
        >
          <Menu className="w-6 h-6" />
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`bg-slate-900 text-white w-80 md:w-80 h-screen md:h-full flex flex-col transition-transform duration-300 ease-in-out
        md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        fixed md:relative z-40`}>

        {/* Fixed Header */}
        <div className="p-3 md:p-4 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Layers className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-semibold">Map Layers</h2>
            </div>
            {/* Close button for mobile */}
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="md:hidden text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-3 md:p-4 space-y-4 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
             style={{ 
               scrollbarWidth: 'thin',
               maxHeight: 'calc(100vh - 80px)', /* Subtract header height */
               minHeight: 0 /* Allow flex shrinking */
             }}>
          
          <div>
            {loading ? (
              <div className="text-center text-gray-400 py-8">
                <Layers className="w-8 h-8 mx-auto mb-2 animate-pulse" />
                <div className="text-sm">Loading layers...</div>
              </div>
            ) : layers.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <div className="text-sm">No layers available</div>
              </div>
            ) : (
              <div className="space-y-2">
                {layers.map(layer => (
                  <LayerButton
                    key={layer.id}
                    layerKey={layer.id}
                    label={layer.description}
                    layerData={layer}
                  />
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default Sidebar;